import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';

export const TIMINGS = [
  {
    key: 'fall/early spring',
    label: 'Fall/ Early Spring (pre-Mar 20)',
    variant: 'fall_early_spring',
  },
  { key: 'spring', label: 'Spring' },
  { key: 'early_sidedress', label: 'Early Sidedress' },
  { key: 'late_sidedress', label: 'Late Sidedress' },
];

export const PRODUCTS = [
  { key: 'instinct-nxtgen', label: 'Instinct NXTGEN' },
  { key: 'instinct', label: 'Instinct' },
  { key: 'guardian-l', label: 'Guardian-L' },
  { key: 'centuro', label: 'Centuro' },
  { key: 'n-serve', label: 'N-Serve' },
  { key: 'function', label: 'Function' },
  { key: 'trident', label: 'Trident' },
];

export function DownloadOptions(props) {
  const { prescriptions, setOptions } = props;

  const availableOptions = useMemo(() => {
    const aaids = prescriptions.map((rx) => rx.aaManagedId);
    const stats = prescriptions
      .filter((rx) => aaids.includes(rx.aaManagedId))
      .map((rx) => rx.stats)
      .filter(Boolean);

    return stats.reduce((memo, current) => {
      Object.entries(current).forEach(([timing, productData]) => {
        memo[timing] = !memo[timing]
          ? [productData.product]
          : [...new Set([...memo[timing], productData.product])];
      });
      return memo;
    }, {});
  }, [prescriptions]);

  const [selectedTiming, setTiming] = useState(null);
  const [selectedProduct, setProduct] = useState(null);

  useEffect(() => {
    setOptions(
      selectedProduct && selectedTiming
        ? { timing: selectedTiming, product: selectedProduct }
        : null,
    );
  }, [selectedProduct, selectedTiming, setOptions]);

  const timings = TIMINGS.filter((t) =>
    Object.keys(availableOptions).includes(t.key),
  );

  const products = PRODUCTS.filter((p) =>
    [
      ...new Set(Object.values(availableOptions).flatMap((option) => option)),
    ].includes(p.key),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Box
        sx={{
          flex: 1,
          marginRight: '1rem',
          paddingRight: '1rem',
          borderRight: '2px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <FormControl>
          <FormLabel sx={{ marginBottom: '1rem' }}>
            Select Timing to Download
          </FormLabel>
          <RadioGroup>
            {timings.map(({ key, label, variant }) => (
              <Button
                key={key}
                variant="contained"
                sx={{
                  justifyContent: 'flex-start',
                  marginBottom: '.5rem',
                  minWidth: '270px',
                }}
                color="inherit"
                onClick={clickHandler(() => {
                  setProduct(null);
                  setTiming(variant || key);
                })}
              >
                <FormControlLabel
                  value={variant || key}
                  control={
                    <Radio checked={selectedTiming === (variant || key)} />
                  }
                  label={label}
                />
              </Button>
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ flex: 1 }}>
        <FormControl>
          <FormLabel sx={{ marginBottom: '1rem' }}>
            Select Product to Download
          </FormLabel>
          <RadioGroup>
            {products.map(({ key, label }) => (
              <Tooltip
                key={key}
                disableHoverListener={!shouldDisableProduct(key)}
                title="Product not available for the selected timing"
              >
                <span>
                  <Button
                    key={key}
                    variant="contained"
                    sx={{
                      justifyContent: 'flex-start',
                      marginBottom: '.5rem',
                      minWidth: '300px',
                    }}
                    color="inherit"
                    onClick={clickHandler(() => {
                      setProduct(key);
                    })}
                    disabled={shouldDisableProduct(key)}
                  >
                    <FormControlLabel
                      value={key}
                      control={<Radio checked={selectedProduct === key} />}
                      label={label}
                    />
                  </Button>
                </span>
              </Tooltip>
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    </Box>
  );

  function shouldDisableProduct(product) {
    const timing = TIMINGS.find(
      (t) => t.key === selectedTiming || t.variant === selectedTiming,
    );
    if (!timing) return true;
    return !(availableOptions[timing.key] || []).includes(product);
  }

  function clickHandler(handler) {
    return (event) => {
      event.stopPropagation();
      handler();
    };
  }
}
